
import React, { useState, useEffect, useRef } from 'react';
import { UserProfile, ReportMetadata, ProjectGlobalData, ConstructionType, ProjectRepresentative, SifaBetriebsdaten } from '../../types';
import { SifaBetriebsdatenFelder } from './SifaBetriebsdatenFelder';
import {
    Building2, ChevronDown, ChevronUp, UserCheck, Contact,
    User, Lock, ShieldCheck, AlertCircle, MapPin, Loader2, Globe, CheckCircle2, LayoutTemplate, Info, XCircle, HardHat,
    ArrowUp, ArrowDown, Trash2, UserPlus
} from 'lucide-react';
import { ProjectCustomerLink } from './ProjectCustomerLink';
import { ProjectSupplementQuoteButton } from './ProjectSupplementQuoteButton';
import { darfNachtragAnlegen, zeigeNachtragKnopf } from '../crm/supplementQuoteEntry';
import { applyRepresentatives, getRepresentatives } from '../../services/projectRepresentatives';
import {
    aendereVertreter, entfernePlanung, entferneVertreter, ergaenzePlanung, ergaenzeVertreter,
    fuegeVerantwortlichkeitenZusammen, teileVerantwortlichkeiten, waehlbareKollegen,
    wechsleZuPlanung, wechsleZuVertreter,
} from './verantwortlichkeitenLogic';
import { planOwnerChange, representativeLabel, ownerTransferNoteText } from '../../services/projectOwnerTransfer';
import { setProjectOwnerColumn } from '../../services/cloudService';
import { resolveProjectCategories, withCurrentValue } from '../../services/projectCategories';
import { buildActorName } from '../../services/actorName';
import { KickoffStatus } from '../kickoff/KickoffStatus';
import { useGesperrteKonten } from '../../hooks/useGesperrteKonten';
import { useRechte } from '../../hooks/useRechte';
import { darfProjektTypWechseln, type AnlageTyp } from '../../services/projektAnlageRechte';
import { ohneGesperrte } from '../../services/gesperrteKonten';
import { istFreimeldungAktiv } from '../../services/freimeldungLogic';

interface Props {
  metadata: ReportMetadata;
  globalData: ProjectGlobalData;
  availableUsers: UserProfile[];
  onUpdate: (report: any) => void;
  report: any; // Full report object for update context
  onShowMap: () => void; // New callback instead of navigation
  isReadOnly?: boolean;
  /** Fuer die CRM-Kundenzuweisung: nur Admins duerfen sie aendern. */
  currentUser?: UserProfile | null;
  /** Sprung in ein Angebot (Nachtragsangebot angelegt). Fehlt = kein Knopf. */
  onOpenQuote?: (quoteId: string) => void;
}

const WEEKDAYS = [
    { label: 'Mo', value: 1 }, { label: 'Di', value: 2 }, { label: 'Mi', value: 3 },
    { label: 'Do', value: 4 }, { label: 'Fr', value: 5 }, { label: 'Sa', value: 6 }, { label: 'So', value: 0 }
];

const OHNE_ZUWEISUNGSRECHT = 'Den Hauptverantwortlichen ändert nur, wer das Recht „Verantwortliche zuweisen“ hat';

export const ProjectMasterData: React.FC<Props> = ({ metadata, globalData, availableUsers, onUpdate, report, onShowMap, isReadOnly = false, currentUser, onOpenQuote }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const gesperrteKonten = useGesperrteKonten();
    // Katalog aus dem Profil (frisch) vor report.settings (Stand beim Laden);
    // ohne beides der eingebaute Standard. Frueher eine Konstante in dieser
    // Datei — jetzt im Firmenbereich pflegbar (services/projectCategories.ts).
    const projectCategories = resolveProjectCategories(currentUser?.settings, report?.settings);
    const aktuelleKategorie = metadata.projectCategory || metadata.category || '';
    const aktuelleUnterkategorie = metadata.projectSubcategory || metadata.subcategory || '';
    const isHse = metadata.projectType === 'hse';
    const isSifa = metadata.projectType === 'sifa';
    // Admin-Erkennung wie im ProjectSetupWizard (ProjectSetupWizard.tsx:112).
    const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'super_admin';
    // Den Hauptverantwortlichen (Position 0) ändern — Besitzerwechsel — und
    // Kickoff-Termine für andere erneut senden: Admin oder Recht
    // `projekte.zuweisen` (Rollensystem, nur Oberfläche). Vertreter und
    // Planung hinzufügen/entfernen darf weiterhin jeder, der das Projekt
    // bearbeiten kann.
    const rechte = useRechte(currentUser?.role);
    const darfZuweisen = isAdmin || rechte.hat('projekte.zuweisen');
    // Kunde (CRM) zuordnen/lösen — schreibt nur `projects.customer_id`.
    // Dieselbe Regel wie im Rechnungsmodul (`canAssignCustomer`).
    const darfKundeZuordnen = isAdmin || rechte.hat('rechnungen.bearbeiten') || rechte.hat('kunden.verwalten');
    // Nachtragsangebot zu diesem Projekt anlegen: Admin oder Recht „Angebote".
    const zeigeNachtrag = zeigeNachtragKnopf({
        darf: darfNachtragAnlegen(isAdmin, rechte.hat),
        isReadOnly,
        hatSprung: Boolean(onOpenQuote),
        projectId: globalData.projectId,
    });
    // Welche Kollegenauswahl offen ist: Vertreter oder Verantwortliche Planungsphase.
    const [offeneAuswahl, setOffeneAuswahl] = useState<'vertreter' | 'planung' | null>(null);
    // Laeuft gerade ein Besitzerwechsel? Sperrt die Pfeile gegen Doppelklicks.
    const [ownerTransferBusy, setOwnerTransferBusy] = useState(false);
    
    // Status für Geocoding: idle, loading, success, error
    const [geoStatus, setGeoStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

    // --- LOKALE STATES FÜR PERFORMANCE-OPTIMIERUNG ---
    const [localProjectName, setLocalProjectName] = useState(metadata.projectName || '');
    
    const [addrParts, setAddrParts] = useState({
        street: '',
        zip: '',
        city: ''
    });

    const [localClient, setLocalClient] = useState({
        name: metadata.clientName || '',
        address: metadata.clientAddress || '',
        contact: metadata.clientContactPerson || '',
        phone: metadata.clientPhone || '',
        email: metadata.clientEmail || ''
    });

    const [localCustomer, setLocalCustomer] = useState({
        name: metadata.customerName || '',
        address: metadata.customerAddress || '',
        contact: metadata.customerContactPerson || '',
        phone: metadata.customerPhone || '',
        email: metadata.customerEmail || ''
    });

    const [localSiteContact, setLocalSiteContact] = useState({
        company: metadata.siteContactCompany || '',
        address: metadata.siteContactAddress || '',
        person: metadata.siteContactPerson || '',
        phone: metadata.siteContactPhone || '',
        email: metadata.siteContactEmail || ''
    });

    // Refs zum Speichern der zuletzt synchronisierten Werte (verhindert Render-Loops)
    const lastSyncedRef = useRef({
        projectName: metadata.projectName || '',
        address: metadata.projectAddress || '',
        client: { ...localClient },
        customer: { ...localCustomer },
        siteContact: { ...localSiteContact }
    });

    const updateTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Initialisierung der lokalen States aus den Props (nur wenn nicht gerade getippt wird)
    useEffect(() => {
        setLocalProjectName(metadata.projectName || '');
        setLocalClient({
            name: metadata.clientName || '',
            address: metadata.clientAddress || '',
            contact: metadata.clientContactPerson || '',
            phone: metadata.clientPhone || '',
            email: metadata.clientEmail || ''
        });
        setLocalCustomer({
            name: metadata.customerName || '',
            address: metadata.customerAddress || '',
            contact: metadata.customerContactPerson || '',
            phone: metadata.customerPhone || '',
            email: metadata.customerEmail || ''
        });
        setLocalSiteContact({
            company: metadata.siteContactCompany || '',
            address: metadata.siteContactAddress || '',
            person: metadata.siteContactPerson || '',
            phone: metadata.siteContactPhone || '',
            email: metadata.siteContactEmail || ''
        });

        // Parse address for addrParts
        if (metadata.projectAddress) {
            let street = '';
            let zip = '';
            let city = '';
            const parts = metadata.projectAddress.split(', ');
            if (parts.length >= 2) {
                street = parts[0] === 'Gelände' ? '' : parts[0];
                const rest = parts.slice(1).join(', ').trim();
                const match = rest.match(/^(\d+)\s+(.*)$/);
                if (match) { zip = match[1]; city = match[2]; }
                else { if (/^\d+$/.test(rest)) zip = rest; else city = rest; }
            } else {
                const singlePart = parts[0];
                if (singlePart !== 'Gelände') {
                    if (/^\d+$/.test(singlePart)) zip = singlePart;
                    else {
                        const match = singlePart.match(/^(\d+)\s+(.*)$/);
                        if (match) { zip = match[1]; city = match[2]; }
                        else { street = singlePart; }
                    }
                }
            }
            setAddrParts({ street, zip, city });
        }

        if (globalData.locationCoordinates?.lat) {
            setGeoStatus('success');
        }

        // Update Sync Ref
        lastSyncedRef.current = {
            projectName: metadata.projectName || '',
            address: metadata.projectAddress || '',
            client: {
                name: metadata.clientName || '',
                address: metadata.clientAddress || '',
                contact: metadata.clientContactPerson || '',
                phone: metadata.clientPhone || '',
                email: metadata.clientEmail || ''
            },
            customer: {
                name: metadata.customerName || '',
                address: metadata.customerAddress || '',
                contact: metadata.customerContactPerson || '',
                phone: metadata.customerPhone || '',
                email: metadata.customerEmail || ''
            },
            siteContact: {
                company: metadata.siteContactCompany || '',
                address: metadata.siteContactAddress || '',
                person: metadata.siteContactPerson || '',
                phone: metadata.siteContactPhone || '',
                email: metadata.siteContactEmail || ''
            }
        };
    }, [metadata.inspectionNumber]); // Re-init on new reports

    // Keep the local address fields in sync when projectAddress changes
    // EXTERNALLY (e.g. set via the "Karte öffnen" location picker). Without this
    // the form keeps showing the old address and its debounced auto-sync would
    // overwrite the freshly picked location. Our own debounced writes are skipped
    // (incoming value already equals what we'd build locally) so typing is not
    // disrupted.
    useEffect(() => {
        const incoming = (metadata.projectAddress || '').trim();
        if (incoming === buildFullAddress().trim()) return;

        let street = '';
        let zip = '';
        let city = '';
        if (incoming) {
            const parts = incoming.split(', ');
            if (parts.length >= 2) {
                street = parts[0] === 'Gelände' ? '' : parts[0];
                const rest = parts.slice(1).join(', ').trim();
                const match = rest.match(/^(\d+)\s+(.*)$/);
                if (match) { zip = match[1]; city = match[2]; }
                else { if (/^\d+$/.test(rest)) zip = rest; else city = rest; }
            } else {
                const singlePart = parts[0];
                if (singlePart !== 'Gelände') {
                    if (/^\d+$/.test(singlePart)) zip = singlePart;
                    else {
                        const match = singlePart.match(/^(\d+)\s+(.*)$/);
                        if (match) { zip = match[1]; city = match[2]; }
                        else { street = singlePart; }
                    }
                }
            }
        }
        setAddrParts({ street, zip, city });
        lastSyncedRef.current.address = incoming;
        if (globalData?.locationCoordinates?.lat) setGeoStatus('success');
    }, [metadata.projectAddress]);

    const constructionTypes: ConstructionType[] = report.settings?.constructionTypes || [];

    // --- SYNCHRONISATIONS LOGIK ---

    const syncToParent = (forceGeocode = false) => {
        const fullAddress = buildFullAddress();
        
        const hasProjectNameChanged = localProjectName !== lastSyncedRef.current.projectName;
        const hasAddressChanged = fullAddress !== lastSyncedRef.current.address;
        const hasClientChanged = JSON.stringify(localClient) !== JSON.stringify(lastSyncedRef.current.client);
        const hasCustomerChanged = JSON.stringify(localCustomer) !== JSON.stringify(lastSyncedRef.current.customer);
        const hasSiteContactChanged = JSON.stringify(localSiteContact) !== JSON.stringify(lastSyncedRef.current.siteContact);

        if (!hasProjectNameChanged && !hasAddressChanged && !hasClientChanged && !hasCustomerChanged && !hasSiteContactChanged) {
            return;
        }

        // Neue Metadaten zusammenbauen
        const newMeta = {
            ...metadata,
            projectName: localProjectName,
            projectAddress: fullAddress,
            clientName: localClient.name,
            clientAddress: localClient.address,
            clientContactPerson: localClient.contact,
            clientPhone: localClient.phone,
            clientEmail: localClient.email,
            customerName: localCustomer.name,
            customerAddress: localCustomer.address,
            customerContactPerson: localCustomer.contact,
            customerPhone: localCustomer.phone,
            customerEmail: localCustomer.email,
            siteContactCompany: localSiteContact.company,
            siteContactAddress: localSiteContact.address,
            siteContactPerson: localSiteContact.person,
            siteContactPhone: localSiteContact.phone,
            siteContactEmail: localSiteContact.email
        };

        const newReport = { ...report, metadata: newMeta };
        onUpdate(newReport);

        // Update Sync Ref
        lastSyncedRef.current = {
            projectName: localProjectName,
            address: fullAddress,
            client: { ...localClient },
            customer: { ...localCustomer },
            siteContact: { ...localSiteContact }
        };

        if (hasAddressChanged && (forceGeocode || addrParts.city || (addrParts.zip && addrParts.zip.length >= 4))) {
            handleGeocodeInternal(fullAddress, newReport);
        }
    };

    const buildFullAddress = () => {
        const street = addrParts.street.trim();
        const zip = addrParts.zip.trim();
        const city = addrParts.city.trim();
        if (street && (zip || city)) return `${street}, ${[zip, city].filter(Boolean).join(' ')}`.trim();
        if (street) return street;
        return [zip, city].filter(Boolean).join(' ').trim();
    };

    // Debounced Fallback (Sicherheitsnetz nach 5s Inaktivität)
    useEffect(() => {
        if (updateTimeoutRef.current) clearTimeout(updateTimeoutRef.current);
        updateTimeoutRef.current = setTimeout(() => {
            syncToParent();
        }, 5000);
        return () => { if (updateTimeoutRef.current) clearTimeout(updateTimeoutRef.current); };
    }, [localProjectName, addrParts, localClient, localCustomer, localSiteContact]);

    const handleGeocodeInternal = async (addressQuery: string, currentReportContext: any) => {
        if (!addressQuery || addressQuery.length < 5) return;
        setGeoStatus('loading');
        try {
            const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(addressQuery)}&limit=1`;
            const res = await fetch(url, { headers: { 'User-Agent': 'SiGeKo-App' } });
            const data = await res.json();
            if (data && data.length > 0) {
                const lat = parseFloat(data[0].lat);
                const lng = parseFloat(data[0].lon);
                onUpdate({
                    ...currentReportContext,
                    metadata: {
                        ...currentReportContext.metadata,
                        globalData: {
                            ...currentReportContext.metadata.globalData,
                            locationCoordinates: { lat, lng }
                        }
                    }
                });
                setGeoStatus('success');
            } else { setGeoStatus('error'); }
        } catch { setGeoStatus('error'); }
    };

    const handleGlobalDataChange = (field: string, value: any) => {
        onUpdate({
            ...report,
            metadata: { ...metadata, globalData: { ...globalData, [field]: value } }
        });
    };

    const handleMetadataDirectChange = (field: string, value: any) => {
        onUpdate({ ...report, metadata: { ...metadata, [field]: value } });
    };

    // Projekttyp umstellen: nur auf Typen mit Anlegen-Recht (Rollensystem,
    // services/projektAnlageRechte.ts) — admin alle, ohne Rechte aus der
    // Datenbank jeder Interne wie bisher.
    const typWaehlbar = (typ: AnlageTyp) => darfProjektTypWechseln(rechte.hat, metadata.projectType, typ);
    const wechsleProjektTyp = (typ: AnlageTyp) => {
        if (!typWaehlbar(typ)) return;
        handleMetadataDirectChange('projectType', typ);
    };

    // Betriebsdaten der SiFa-Betreuung: ein Update je Patch, gemischt in
    // metadata.sifa — dasselbe Muster wie handleMetadataDirectChange, nur eine
    // Ebene tiefer. Die Felder selbst rufen es je Blur genau einmal.
    const handleSifaPatch = (patch: Partial<SifaBetriebsdaten>) => {
        onUpdate({ ...report, metadata: { ...metadata, sifa: { ...(metadata.sifa || {}), ...patch } } });
    };

    // --- VERANTWORTLICHE (sortierbare Liste, Index 0 = Hauptverantwortlicher) ---
    //
    // Drei Rollen auf EINER gespeicherten Liste (docs/projekt-kickoff-plan.md
    // §5, Entscheid 12.10.): Hauptverantwortlicher (Position 0), Vertreter und
    // Verantwortliche Planungsphase (`bereich: 'planung'`). Die Oberfläche teilt
    // die Liste auf und fügt sie beim Speichern wieder zusammen — Regeln in
    // verantwortlichkeitenLogic.ts. Pfeile und Besitzerwechsel arbeiten nur auf
    // `ausfuehrung`: eine Planungsperson ist nie Kandidat für Position 0.

    const representatives = getRepresentatives(metadata);
    const { ausfuehrung, planung } = teileVerantwortlichkeiten(representatives);

    // Kalender-Quittung (Projekt-Kickoff): „Termine erneut senden" dürfen der
    // Besitzer (= Hauptverantwortlicher, Index 0) und wer Verantwortliche
    // zuweisen darf (Admin oder Recht `projekte.zuweisen`).
    const darfKickoffSenden = !isReadOnly && (darfZuweisen || (!!currentUser?.id && representatives[0]?.id === currentUser.id));
    const kickoffNameFuer = (recipientId: string): string | undefined =>
        buildActorName(availableUsers.find(u => u.id === recipientId))
        || representatives.find(r => r.id === recipientId)?.name
        || undefined;

    const commitRepresentatives = (next: ProjectRepresentative[]) => {
        onUpdate({ ...report, metadata: applyRepresentatives(metadata, next) });
    };

    const handleAddRepresentative = (user: UserProfile) => {
        // Werte 1:1 aus dem Profil übernehmen — auch leere. Ein Fallback auf den
        // Vorgänger liesse dessen Nummer/E-Mail bei einem Personenwechsel stehen.
        commitRepresentatives(ergaenzeVertreter(representatives, user));
        setOffeneAuswahl(null);
    };

    const handleAddPlanung = (user: UserProfile) => {
        commitRepresentatives(ergaenzePlanung(representatives, user));
        setOffeneAuswahl(null);
    };

    // `index` zählt in `ausfuehrung`. Position 0 wird nie entfernt, nur durch
    // Hochschieben eines anderen ersetzt (entferneVertreter prüft das selbst).
    const handleRemoveRepresentative = (index: number) => {
        if (index === 0) return;
        commitRepresentatives(entferneVertreter(representatives, index));
    };

    const handleRemovePlanung = (index: number) => {
        commitRepresentatives(entfernePlanung(representatives, index));
    };

    // Rollenwechsel ohne Entfernen und neu Hinzufügen — so bleiben auch
    // Einträge ohne Benutzerkonto (Freitext-Altdaten) erhalten, die über die
    // Kollegenauswahl gar nicht wieder hineinkämen. Der Bereich wird dabei
    // gesetzt bzw. entfernt (verantwortlichkeitenLogic.ts).
    const handleWechselZuPlanung = (index: number) => {
        if (index === 0) return;
        commitRepresentatives(wechsleZuPlanung(representatives, index));
    };

    const handleWechselZuVertreter = (index: number) => {
        commitRepresentatives(wechsleZuVertreter(representatives, index));
    };

    /**
     * Verschiebt einen Eintrag — und behandelt den Sprung auf Position 0 als
     * das, was er ist: ein Besitzerwechsel.
     *
     * Bis 20.08.2026 schrieb dieser Pfad nur den Bericht. `projects.user_id`
     * blieb beim bisherigen Verantwortlichen zurueck, und die Admin-
     * Projektliste meldete den Unterschied Tage spaeter als roten „Fix" —
     * obwohl niemand etwas falsch gemacht hatte (Vorfall 26081015). Der Zug
     * fuehrt die Spalte deshalb jetzt mit, samt Rueckfrage und Protokollnotiz.
     *
     * Reihenfolge: erst die Datenbank, dann der lokale Bericht. Schlaegt der
     * Schreibvorgang fehl, bleibt alles wie es war — die Umkehrung hinterliesse
     * eine Notiz ueber einen Wechsel, den es nie gab.
     *
     * `index` zaehlt in `ausfuehrung` (Hauptverantwortlicher + Vertreter). Die
     * Verantwortlichen Planungsphase sind nicht Teil dieser Liste und damit
     * nie Kandidat fuer Position 0; sie werden beim Speichern unveraendert
     * hinten angehaengt (fuegeVerantwortlichkeitenZusammen).
     */
    const handleMoveRepresentative = async (index: number, direction: -1 | 1) => {
        if (isReadOnly || ownerTransferBusy) return;
        // Nur wer Verantwortliche zuweisen darf (Admin oder Recht
        // `projekte.zuweisen`), veraendert den Hauptverantwortlichen (Index 0):
        // Hochschieben auf Index 0 oder Herunterschieben von Index 0 ist fuer
        // alle anderen gesperrt. Guard hier zusaetzlich zum JSX-Disable, damit
        // kein Aufrufer (auch kuenftiger) daran vorbeikommt.
        if (!darfZuweisen && (index === 0 || index + direction === 0)) return;

        const plan = planOwnerChange(ausfuehrung, index, direction);
        if (plan.next === ausfuehrung) return;
        const nextList = fuegeVerantwortlichkeitenZusammen({ ausfuehrung: plan.next, planung });
        if (!plan.changesOwner) { commitRepresentatives(nextList); return; }

        const fromName = representativeLabel(plan.previousOwner);
        const toName = representativeLabel(plan.newOwner);

        // Ohne Benutzerkonto laesst sich `projects.user_id` nicht setzen. Den
        // Zug trotzdem auszufuehren hiesse, den Konflikt bewusst zu erzeugen.
        if (!plan.transferTargetId) {
            alert(
                `„${toName}" ist keinem Benutzerkonto zugeordnet.\n\n`
                + 'Der Hauptverantwortliche im Bericht ließe sich ändern, der Projekt-Besitzer in der '
                + 'Datenbank aber nicht — beides liefe auseinander. Bitte die Person über '
                + '„Person hinzufügen" aus der Kollegenliste wählen.'
            );
            return;
        }

        const projectId = metadata.globalData?.projectId || globalData?.projectId;
        if (!projectId || projectId === 'temp-id') {
            alert('Das Projekt ist noch nicht gespeichert. Bitte einmal speichern und den Wechsel dann wiederholen.');
            return;
        }

        if (!confirm(
            `Hauptverantwortlich wird ${toName} statt ${fromName}.\n\n`
            + `Das ist ein Besitzerwechsel: ${toName} wird auch in der Datenbank als verantwortlicher `
            + 'Koordinator eingetragen und der Wechsel im Projektprotokoll vermerkt.\n\nFortfahren?'
        )) return;

        setOwnerTransferBusy(true);
        try {
            await setProjectOwnerColumn(projectId, plan.transferTargetId, toName);

            const actorName = currentUser
                ? `${currentUser.first_name || ''} ${currentUser.last_name || ''}`.trim()
                : '';
            const note = {
                id: crypto.randomUUID(),
                text: ownerTransferNoteText(fromName, toName),
                date: new Date().toISOString(),
                author: actorName || 'System',
                mentions: [plan.transferTargetId],
                readBy: []
            };

            const nextMeta = applyRepresentatives(metadata, nextList);
            onUpdate({
                ...report,
                metadata: {
                    ...nextMeta,
                    globalData: {
                        ...(nextMeta.globalData || globalData),
                        notes: [note, ...(nextMeta.globalData?.notes || globalData?.notes || [])]
                    }
                }
            });
        } catch (e: any) {
            alert('Besitzerwechsel fehlgeschlagen: ' + (e?.message || 'Unbekannter Fehler') + '\n\nEs wurde nichts geändert.');
        } finally {
            setOwnerTransferBusy(false);
        }
    };

    // `index` zählt in `ausfuehrung` — nur Vertreter haben Einsatztage.
    const patchRepresentative = (index: number, patch: Partial<ProjectRepresentative>) => {
        commitRepresentatives(aendereVertreter(representatives, index, patch));
    };

    const handleRepresentativeWeekdayChange = (index: number, day: number) => {
        const selectedDays = ausfuehrung[index]?.weekdays || [];
        const isSelected = selectedDays.includes(day);
        const next = isSelected ? selectedDays.filter(d => d !== day) : [...selectedDays, day].sort();
        patchRepresentative(index, { weekdays: next });
    };

    // Bereits gelistete Personen (in jeder Rolle) tauchen in keiner Auswahl
    // noch einmal auf — eine Person hat genau eine Rolle. Gesperrte
    // Mitglieder (Nr. 148) werden nicht mehr angeboten; wer schon eingetragen
    // ist, bleibt in seiner Rolle stehen und lässt sich entfernen.
    const selectableUsers = ohneGesperrte(waehlbareKollegen(availableUsers, representatives), gesperrteKonten);
    // SiFa kennt keine Planungsphase: der Abschnitt erscheint dort nur, um
    // Einträge aus der Zeit vor einem Typwechsel noch entfernen zu können.
    const zeigePlanung = !isSifa || planung.length > 0;
    // Ohne Hauptverantwortlichen würde die erste Planungsperson Besitzer —
    // erst muss Position 0 stehen.
    const darfPlanungErgaenzen = ausfuehrung.length > 0 && !isSifa;

    const renderWeekdayPicker = (index: number) => {
        const selectedDays = ausfuehrung[index]?.weekdays || [];
        return (
            <div className="flex gap-1 mt-2">
                {WEEKDAYS.map(day => {
                    const isSelected = selectedDays.includes(day.value);
                    return (
                        <button key={day.value} type="button" onClick={() => handleRepresentativeWeekdayChange(index, day.value)} className={`w-6 h-6 rounded text-[10px] border transition-all flex items-center justify-center ${isSelected ? 'bg-blue-50 text-blue-600 border-blue-200 font-bold shadow-sm' : 'bg-white text-slate-500 border-slate-200 hover:border-blue-300'}`}>
                            {day.label.substring(0, 1)}
                        </button>
                    );
                })}
            </div>
        );
    };

    const abschnittsTitel = 'text-[11px] font-bold uppercase tracking-wide text-slate-500 px-1';

    /** Name + Kontaktzeile, gleich für alle drei Rollen. */
    const renderPerson = (rep: ProjectRepresentative) => (
        <>
            <div className="flex items-center gap-2 mt-1.5">
                {rep.id ? <ShieldCheck className="w-4 h-4 text-emerald-600 shrink-0" /> : <AlertCircle className="w-4 h-4 text-slate-300 shrink-0" />}
                <span className="font-bold text-sm text-slate-800 truncate">{rep.name || rep.email || 'Ohne Namen'}</span>
                {rep.id && gesperrteKonten.has(rep.id) && (
                    <span className="shrink-0 px-1.5 py-0.5 rounded text-[10px] font-bold bg-red-100 text-red-700" title="Dieses Mitglied ist gesperrt (Benutzerverwaltung) — bitte die Rolle neu besetzen.">Gesperrt</span>
                )}
            </div>
            <p className="text-[11px] text-slate-500 truncate mt-0.5 pl-6">{[rep.email, rep.phone].filter(Boolean).join(' · ') || 'Keine Kontaktdaten im Profil'}</p>
        </>
    );

    /**
     * Karte des Hauptverantwortlichen (index 0) oder eines Vertreters.
     * `index` zählt in `ausfuehrung` — die Pfeile verschieben nur innerhalb
     * dieser Liste; der Sprung auf/von Position 0 ist der Besitzerwechsel.
     */
    const renderAusfuehrungKarte = (rep: ProjectRepresentative, index: number) => (
        <div key={`${rep.id || rep.email || rep.name}-${index}`} className="bg-white rounded-xl border border-slate-200 p-3 shadow-sm">
            <div className="flex items-start gap-2">
                <div className="flex flex-col gap-1 pt-0.5">
                    <button type="button" title={index === 1 ? (darfZuweisen ? 'Nach oben — macht diese Person zum Hauptverantwortlichen (Besitzerwechsel)' : OHNE_ZUWEISUNGSRECHT) : 'Nach oben'} disabled={index === 0 || ownerTransferBusy || (!darfZuweisen && index === 1)} onClick={() => void handleMoveRepresentative(index, -1)} className="w-6 h-5 rounded border border-slate-200 text-slate-500 flex items-center justify-center transition-colors hover:border-blue-300 hover:text-blue-600 disabled:opacity-30 disabled:cursor-not-allowed"><ArrowUp className="w-3 h-3" /></button>
                    <button type="button" title={index === 0 ? (darfZuweisen ? 'Nach unten — gibt den Hauptverantwortlichen an den ersten Vertreter ab (Besitzerwechsel)' : OHNE_ZUWEISUNGSRECHT) : 'Nach unten'} disabled={index === ausfuehrung.length - 1 || ownerTransferBusy || (!darfZuweisen && index === 0)} onClick={() => void handleMoveRepresentative(index, 1)} className="w-6 h-5 rounded border border-slate-200 text-slate-500 flex items-center justify-center transition-colors hover:border-blue-300 hover:text-blue-600 disabled:opacity-30 disabled:cursor-not-allowed"><ArrowDown className="w-3 h-3" /></button>
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-center gap-2">
                        <span className={`text-[11px] font-black uppercase tracking-wide px-1.5 py-0.5 rounded border ${index === 0 ? (isSifa ? 'bg-teal-50 text-teal-700 border-teal-200' : 'bg-blue-50 text-blue-700 border-blue-200') : 'bg-slate-50 text-slate-500 border-slate-200'}`}>
                            {index === 0 ? (isHse ? 'HSSE Manager' : 'Hauptverantwortlich') : `${isHse ? 'HSSE Deputy' : 'Stellvertreter'} ${index}`}
                        </span>
                        {/* Kein Aktiv-Haken mehr: wer in der Liste steht, ist zuständig.
                            Das Label beschriftet nur den Wochentags-Picker darunter —
                            bei SiFa gibt es keine Einsatztage (kein Wochenrhythmus). */}
                        {index > 0 && !isSifa && (
                            <span className="text-[11px] font-bold uppercase text-slate-400">Einsatztage</span>
                        )}
                    </div>
                    {renderPerson(rep)}
                    {index > 0 && !isSifa && renderWeekdayPicker(index)}
                    {index > 0 && !isSifa && (
                        <button type="button" onClick={() => handleWechselZuPlanung(index)} className="mt-2 text-[11px] font-bold text-slate-400 hover:text-blue-600 transition-colors">
                            In Planungsphase verschieben
                        </button>
                    )}
                </div>
                {index === 0
                    ? <div className="text-slate-300 pt-0.5" title="Der Hauptverantwortliche wird nur durch Hochschieben einer anderen Person ersetzt."><Lock className="w-4 h-4" /></div>
                    : <button type="button" title="Entfernen" onClick={() => handleRemoveRepresentative(index)} className="text-slate-300 hover:text-red-500 transition-colors pt-0.5"><Trash2 className="w-4 h-4" /></button>}
            </div>
        </div>
    );

    /** Karte einer Verantwortlichen Planungsphase (`index` zählt in `planung`). */
    const renderPlanungKarte = (rep: ProjectRepresentative, index: number) => (
        <div key={`planung-${rep.id || rep.email || rep.name}-${index}`} className="bg-white rounded-xl border border-slate-200 p-3 shadow-sm">
            <div className="flex items-start gap-2">
                <div className="flex-1 min-w-0">
                    <span className="text-[11px] font-black uppercase tracking-wide px-1.5 py-0.5 rounded border bg-indigo-50 text-indigo-700 border-indigo-200">
                        Planungsphase
                    </span>
                    {renderPerson(rep)}
                    <button type="button" onClick={() => handleWechselZuVertreter(index)} className="mt-2 text-[11px] font-bold text-slate-400 hover:text-blue-600 transition-colors">
                        Zum Vertreter machen
                    </button>
                </div>
                <button type="button" title="Entfernen" onClick={() => handleRemovePlanung(index)} className="text-slate-300 hover:text-red-500 transition-colors pt-0.5"><Trash2 className="w-4 h-4" /></button>
            </div>
        </div>
    );

    /** „Person hinzufügen" mit Kollegenauswahl — je Rolle eine eigene. */
    const renderKollegenAuswahl = (art: 'vertreter' | 'planung', onPick: (user: UserProfile) => void, label: string) => (
        <div className="relative user-dropdown-container">
            <button type="button" onClick={() => setOffeneAuswahl(offeneAuswahl === art ? null : art)} className="w-full px-3 py-2 rounded-lg border border-dashed border-slate-300 text-xs font-bold text-slate-500 hover:border-blue-400 hover:text-blue-600 transition-colors flex items-center justify-center gap-2 bg-white/60">
                <UserPlus className="w-4 h-4" /> {label}
            </button>
            {offeneAuswahl === art && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-xl z-20 max-h-48 overflow-y-auto animate-fade-in user-dropdown-container">
                    {selectableUsers.length === 0 ? <div className="p-3 text-xs text-slate-400 text-center italic">Keine weiteren Kollegen gefunden.</div> : selectableUsers.map(u => (
                        <div key={u.id} onClick={() => onPick(u)} className="px-3 py-2 hover:bg-blue-50 cursor-pointer text-sm text-slate-700 border-b border-slate-50 last:border-0 flex items-center justify-between group">
                            <span className="font-medium">{u.first_name} {u.last_name}</span><span className="text-[11px] text-slate-400 group-hover:text-blue-500">{u.email}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );

    const selectedConstructionType = constructionTypes.find(t => t.id === metadata.constructionType);

    return (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 hover:shadow-md transition-shadow duration-300">
             <button data-tutorial="hub.stammdaten" onClick={() => setIsExpanded(!isExpanded)} className={`w-full text-left border-b border-slate-100 px-6 py-4 flex items-center justify-between rounded-t-2xl transition-colors ${isHse ? 'bg-emerald-50/50 hover:bg-emerald-100/50' : 'bg-slate-50/50 hover:bg-slate-100/50'}`}>
                <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${isExpanded ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-500'}`}><Building2 className="w-5 h-5" /></div>
                    <div><h2 className={`text-lg font-bold ${isHse ? 'text-emerald-800' : 'text-slate-800'}`}>Projekt-Stammdaten</h2>{!isExpanded && <p className="text-xs text-slate-500">{metadata.projectName} | {isSifa
                        ? ((metadata.customerName || metadata.clientName) ? `Betrieb: ${metadata.customerName || metadata.clientName}` : 'Kein Betrieb')
                        : (metadata.clientName ? `Bauherr: ${metadata.clientName}` : 'Kein Bauherr')}</p>}</div>
                </div>
                <div className="flex items-center gap-4">
                    {!isExpanded && <div className="hidden md:flex bg-white rounded-lg p-1 border border-slate-200 shadow-sm"><span className="text-xs px-2 py-1 font-bold text-slate-600">{metadata.projectType === 'hse' ? 'HSSE' : isSifa ? 'SiFa' : 'SiGeKo'}</span></div>}
                    <div className={`p-2 rounded-full transition-colors ${isExpanded ? 'bg-slate-100 text-slate-600' : 'text-slate-400'}`}>{isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}</div>
                </div>
            </button>
            
            {isExpanded && (
                <div className={`p-6 grid grid-cols-1 md:grid-cols-2 gap-8 animate-in slide-in-from-top-2 duration-200 ${isReadOnly ? 'pointer-events-none select-none opacity-80' : ''}`}>
                    <div className="space-y-6">
                        <div className="space-y-1">
                            <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">Projektname</label>
                            <input 
                                type="text" 
                                value={localProjectName} 
                                onChange={(e) => setLocalProjectName(e.target.value)} 
                                onBlur={() => syncToParent()}
                                className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:ring-4 focus:ring-blue-50 focus:border-blue-500 font-bold text-slate-800 transition-all outline-none shadow-inner" 
                                placeholder="z.B. Neubau Wohnpark" 
                            />
                        </div>

                        {/* SiFa: der Betrieb statt Bau-Kategorie, Bauart, Volumen und
                            Bauherr — eine Betreuung hat keine Baustelle. */}
                        {isSifa && <SifaBetriebsdatenFelder sifa={metadata.sifa} onPatch={handleSifaPatch} isReadOnly={isReadOnly} />}

                        {/* Kategorie & Unterkategorie Selection */}
                        {!isSifa && <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <label className="text-xs font-bold text-slate-500 uppercase tracking-wide flex items-center gap-1">
                                    <LayoutTemplate className="w-3.5 h-3.5 text-blue-600" /> Kategorie
                                </label>
                                <div className="relative">
                                    <select 
                                        value={aktuelleKategorie}
                                        disabled={isReadOnly}
                                        onChange={(e) => {
                                            const cat = e.target.value;
                                            const subs = projectCategories[cat] || [];
                                            const sub = subs.length > 0 ? subs[0] : '';
                                            onUpdate({
                                                ...report,
                                                metadata: {
                                                    ...metadata,
                                                    projectCategory: cat,
                                                    category: cat,
                                                    projectSubcategory: sub,
                                                    subcategory: sub
                                                }
                                            });
                                        }} 
                                        className="w-full px-4 py-2.5 border border-slate-300 rounded-xl bg-white focus:ring-2 focus:ring-blue-500 outline-none text-sm font-medium appearance-none shadow-sm disabled:bg-slate-100 disabled:text-slate-400"
                                    >
                                        <option value="">-- Keine Kategorie --</option>
                                        {withCurrentValue(Object.keys(projectCategories), aktuelleKategorie).map(cat => (
                                            <option key={cat} value={cat}>{cat}</option>
                                        ))}
                                    </select>
                                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <label className="text-xs font-bold text-slate-500 uppercase tracking-wide flex items-center gap-1">
                                    <LayoutTemplate className="w-3.5 h-3.5 text-blue-600" /> Unterkategorie
                                </label>
                                <div className="relative">
                                    <select 
                                        value={aktuelleUnterkategorie}
                                        onChange={(e) => {
                                            const sub = e.target.value;
                                            onUpdate({
                                                ...report,
                                                metadata: {
                                                    ...metadata,
                                                    projectSubcategory: sub,
                                                    subcategory: sub
                                                }
                                            });
                                        }}
                                        disabled={isReadOnly || !aktuelleKategorie}
                                        className="w-full px-4 py-2.5 border border-slate-300 rounded-xl bg-white focus:ring-2 focus:ring-blue-500 outline-none text-sm font-medium appearance-none shadow-sm disabled:bg-slate-100 disabled:text-slate-400"
                                    >
                                        <option value="">-- Keine Unterkategorie --</option>
                                        {withCurrentValue(aktuelleKategorie ? (projectCategories[aktuelleKategorie] || []) : [], aktuelleUnterkategorie).map(sub => (
                                            <option key={sub} value={sub}>{sub}</option>
                                        ))}
                                    </select>
                                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                                </div>
                            </div>
                        </div>}

                        {!isSifa && constructionTypes.length > 0 && (
                            <div className="space-y-1">
                                <label className="text-xs font-bold text-slate-500 uppercase tracking-wide flex items-center gap-1"><LayoutTemplate className="w-3 h-3"/> Bauart</label>
                                <div className="relative">
                                    <select value={metadata.constructionType || ''} onChange={(e) => handleMetadataDirectChange('constructionType', e.target.value)} className="w-full px-4 py-2 border border-slate-300 rounded-xl bg-white focus:ring-2 focus:ring-blue-500 outline-none text-sm font-medium appearance-none">
                                        <option value="">-- Nicht festgelegt --</option>
                                        {constructionTypes.map(t => (
                                            <option key={t.id} value={t.id}>{t.title}</option>
                                        ))}
                                    </select>
                                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                                </div>
                                {selectedConstructionType && (
                                    <div className="flex items-start gap-2 bg-blue-50/50 p-2 rounded-lg text-xs text-slate-600 border border-blue-100">
                                        <Info className="w-3 h-3 mt-0.5 text-blue-500 shrink-0" />
                                        <span>{selectedConstructionType.description}</span>
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="space-y-4">
                            <div className="flex justify-between items-center">
                                <label className="text-xs font-bold text-slate-500 uppercase tracking-wide flex items-center gap-2">Standort & Adresse {geoStatus === 'success' && <span className="flex items-center gap-1 bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded text-[10px] font-black border border-emerald-200 animate-in fade-in zoom-in"><ShieldCheck className="w-3 h-3" /> Verifiziert</span>} {geoStatus === 'error' && <span className="flex items-center gap-1 bg-red-100 text-red-700 px-1.5 py-0.5 rounded text-[10px] font-black border border-red-200 animate-in fade-in zoom-in"><XCircle className="w-3 h-3" /> Nicht gefunden</span>}</label>
                                <button onClick={onShowMap} className="text-[11px] font-black text-blue-600 hover:text-blue-800 flex items-center gap-1 bg-blue-50 px-2 py-1 rounded-lg border border-blue-100 transition-all shadow-sm"><MapPin className="w-3.5 h-3.5" /> Karte öffnen</button>
                            </div>
                            <div className="bg-slate-50 p-4 rounded-2xl border border-slate-200 space-y-3 shadow-inner relative">
                                <div className="grid grid-cols-3 gap-2">
                                    <div className="col-span-2 space-y-1">
                                        <span className="text-[10px] font-bold text-slate-400 uppercase ml-1">Straße & Nr.</span>
                                        <input type="text" value={addrParts.street} onChange={e => setAddrParts(prev => ({ ...prev, street: e.target.value }))} onBlur={() => syncToParent(true)} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 outline-none" placeholder="Straße & Nr." />
                                    </div>
                                    <div className="space-y-1">
                                        <span className="text-[10px] font-bold text-slate-400 uppercase ml-1">PLZ</span>
                                        <input type="text" value={addrParts.zip} onChange={e => setAddrParts(prev => ({ ...prev, zip: e.target.value }))} onBlur={() => syncToParent(true)} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 outline-none font-mono" placeholder="12345" />
                                    </div>
                                </div>
                                <div className="space-y-1">
                                    <span className="text-[10px] font-bold text-slate-400 uppercase ml-1">Ort / Stadt</span>
                                    <div className="relative">
                                        <input type="text" value={addrParts.city} onChange={e => setAddrParts(prev => ({ ...prev, city: e.target.value }))} onBlur={() => syncToParent(true)} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 outline-none" placeholder="Stadt eingeben..." />
                                        {geoStatus === 'loading' && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-blue-500" />}
                                        {geoStatus === 'success' && <CheckCircle2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-emerald-500" />}
                                        {geoStatus === 'error' && <AlertCircle className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-red-500" />}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="space-y-1">
                                <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">Proj. Nr.</label>
                                <input type="text" value={metadata.projectNumber || ''} readOnly className="w-full px-4 py-2 border border-slate-300 rounded-xl font-mono outline-none bg-slate-100 text-slate-500 cursor-not-allowed shadow-inner" />
                            </div>
                            {!isSifa && <div className="space-y-1">
                                <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">Gesamt-Projektvolumen (€)</label>
                                <input 
                                    type="number" 
                                    value={metadata.totalProjectVolume !== undefined ? metadata.totalProjectVolume : ''} 
                                    onChange={(e) => {
                                        const val = e.target.value === '' ? undefined : Number(e.target.value);
                                        handleMetadataDirectChange('totalProjectVolume', val);
                                    }}
                                    className="w-full px-4 py-2 border border-slate-300 rounded-xl outline-none focus:ring-4 focus:ring-blue-50 focus:border-blue-500 transition-all shadow-inner font-mono font-bold text-slate-800" 
                                    placeholder="z.B. 800000000"
                                />
                                {metadata.totalProjectVolume !== undefined && metadata.totalProjectVolume > 0 && (
                                    <p className="text-xs text-slate-500 font-bold px-1 mt-1 font-mono">
                                        = {new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(metadata.totalProjectVolume)}
                                    </p>
                                )}
                            </div>}
                        </div>

                        {!isSifa && <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 shadow-inner">
                            <h4 className="text-sm font-bold text-slate-700 flex items-center gap-2 mb-3"><User className="w-4 h-4 text-blue-600"/> Bauherr</h4>
                            <div className="space-y-3">
                                <div className="space-y-1"><label className="text-[11px] font-bold text-slate-400 uppercase">Firma / Name</label><input type="text" value={localClient.name} onChange={(e) => setLocalClient({...localClient, name: e.target.value})} onBlur={() => syncToParent()} className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:border-blue-500 outline-none bg-white text-sm" placeholder="Musterfirma GmbH" /></div>
                                <div className="space-y-1"><label className="text-[11px] font-bold text-slate-400 uppercase">Anschrift</label><input type="text" value={localClient.address} onChange={(e) => setLocalClient({...localClient, address: e.target.value})} onBlur={() => syncToParent()} className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:border-blue-500 outline-none bg-white text-sm" placeholder="Straße, PLZ Ort" /></div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="space-y-1"><label className="text-[11px] font-bold text-slate-400 uppercase">Ansprechpartner</label><input type="text" value={localClient.contact} onChange={(e) => setLocalClient({...localClient, contact: e.target.value})} onBlur={() => syncToParent()} className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:border-blue-500 outline-none bg-white text-sm" /></div>
                                    <div className="space-y-1"><label className="text-[11px] font-bold text-slate-400 uppercase">Telefon</label><input type="text" value={localClient.phone} onChange={(e) => setLocalClient({...localClient, phone: e.target.value})} onBlur={() => syncToParent()} className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:border-blue-500 outline-none bg-white text-sm" /></div>
                                </div>
                                <div className="space-y-1"><label className="text-[11px] font-bold text-slate-400 uppercase">E-Mail</label><input type="text" value={localClient.email} onChange={(e) => setLocalClient({...localClient, email: e.target.value})} onBlur={() => syncToParent()} className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:border-blue-500 outline-none bg-white text-sm" /></div>
                            </div>
                        </div>}
                    </div>

                    <div className="space-y-6">
                        <div className="flex bg-white rounded-lg p-1 border border-slate-200 shadow-sm w-fit">
                            {/* Kein projectType = SiGeKo (Altbestand). 'sifa' schaltet
                                den Hub auf das SiFa-Raster (SifaModuleGrid) um.
                                Angeboten werden nur Typen, auf die man umstellen darf
                                (Anlegen-Recht des neuen Typs, sonst weist die
                                Datenbank Nr. 154 das Speichern ab); der aktuelle
                                Typ steht immer da. */}
                            {typWaehlbar('sigeko') && <button onClick={() => wechsleProjektTyp('sigeko')} className={`px-3 py-1.5 rounded-md text-xs font-bold transition-all flex items-center gap-1 ${metadata.projectType !== 'hse' && metadata.projectType !== 'sifa' ? 'bg-blue-600 text-white shadow' : 'text-slate-500 hover:text-slate-700'}`}>SiGeKo</button>}
                            {typWaehlbar('hse') && <button onClick={() => wechsleProjektTyp('hse')} className={`px-3 py-1.5 rounded-md text-xs font-bold transition-all flex items-center gap-1 ${metadata.projectType === 'hse' ? 'bg-emerald-600 text-white shadow' : 'text-slate-500 hover:text-slate-700'}`}>HSSE</button>}
                            {typWaehlbar('sifa') && <button onClick={() => wechsleProjektTyp('sifa')} title="Fachkraft für Arbeitssicherheit — unbefristete Betreuung" className={`px-3 py-1.5 rounded-md text-xs font-bold transition-all flex items-center gap-1 ${isSifa ? 'bg-teal-600 text-white shadow' : 'text-slate-500 hover:text-slate-700'}`}>SiFa</button>}
                        </div>

                        <div data-tutorial="beteiligte.vertreter" className={`space-y-4 p-5 rounded-2xl border transition-colors shadow-inner ${isHse ? 'bg-emerald-50/50 border-emerald-100 hover:border-emerald-200' : isSifa ? 'bg-teal-50/50 border-teal-100 hover:border-teal-200' : 'bg-slate-50/50 border-slate-100 hover:border-blue-200'}`}>
                            <h3 className={`text-xs font-bold uppercase flex items-center gap-2 mb-3 ${isHse ? 'text-emerald-600' : isSifa ? 'text-teal-600' : 'text-blue-600'}`}><UserCheck className="w-4 h-4" /> Verantwortlichkeiten</h3>
                            {/* 1. Hauptverantwortlicher (Position 0, Besitzer, Ausführung) */}
                            <div className="space-y-2">
                                <p className={abschnittsTitel}>{isHse ? 'HSSE Manager' : 'Hauptverantwortlicher'}</p>
                                {ausfuehrung.length === 0
                                    ? <p className="text-xs italic text-slate-400 px-1">Noch niemand zugeordnet.</p>
                                    : renderAusfuehrungKarte(ausfuehrung[0], 0)}
                            </div>

                            {/* 2. Verantwortliche Planungsphase (bereich 'planung', mehrere möglich).
                                Keine Pfeile: diese Personen sind nie Kandidat für Position 0. */}
                            {zeigePlanung && (
                                <div className="space-y-2">
                                    <p className={abschnittsTitel}>Verantwortliche Planungsphase</p>
                                    <p className="text-[11px] text-slate-500 px-1 -mt-1">
                                        Zuständig für Vorankündigung, Unterlage, SiGe-Plan, A+S-Plan und Planung — keine Begehungen.
                                        {planung.length === 0 && ' Leer = der Hauptverantwortliche plant selbst.'}
                                    </p>
                                    {planung.map((rep, index) => renderPlanungKarte(rep, index))}
                                    {darfPlanungErgaenzen && renderKollegenAuswahl('planung', handleAddPlanung, 'Planungsperson hinzufügen')}
                                </div>
                            )}

                            {/* 3. Vertreter (wie bisher, ohne Planungs-Einträge) */}
                            {ausfuehrung.length > 0 && (
                                <div className="space-y-2">
                                    <p className={abschnittsTitel}>{isHse ? 'HSSE Deputies' : 'Vertreter'}</p>
                                    {ausfuehrung.slice(1).map((rep, i) => renderAusfuehrungKarte(rep, i + 1))}
                                </div>
                            )}
                            {renderKollegenAuswahl('vertreter', handleAddRepresentative, ausfuehrung.length === 0 ? 'Person hinzufügen' : 'Vertreter hinzufügen')}
                            {/* Ob die Zuständigen ihre Termine im Kalender haben
                                (docs/projekt-kickoff-plan.md §4.4). Ohne Quittung
                                (Altprojekt, Migration fehlt) rendert das nichts. */}
                            <KickoffStatus
                                projectId={globalData?.projectId || metadata.globalData?.projectId}
                                darfErneutSenden={darfKickoffSenden}
                                nameFuer={kickoffNameFuer}
                            />
                        </div>

                        <div className="bg-indigo-50 p-5 rounded-2xl border border-indigo-100 shadow-inner">
                            <h4 className="text-xs font-bold text-indigo-900 flex items-center gap-2 mb-4"><Globe className="w-4 h-4 text-indigo-600" /> Versand-Konfiguration</h4>
                            <div className="space-y-4">
                                <div className="space-y-1"><label className="text-[11px] font-bold text-slate-400 uppercase tracking-widest ml-1">Versand-Methode</label><select value={globalData.deliveryConfig?.method || 'email'} onChange={(e) => handleGlobalDataChange('deliveryConfig', { ...globalData.deliveryConfig, method: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none bg-white text-sm font-medium"><option value="email">E-Mail (Standard)</option><option value="poolarserver">Poolarserver (Link)</option><option value="both">Beides (E-Mail + Poolar)</option></select></div>
                                {(globalData.deliveryConfig?.method === 'poolarserver' || globalData.deliveryConfig?.method === 'both') && (
                                    <div className="space-y-1 animate-in slide-in-from-top-1 duration-200"><label className="text-[11px] font-bold text-slate-400 uppercase tracking-widest ml-1">Poolarserver URL</label><input type="url" value={globalData.deliveryConfig?.poolarserverUrl || ''} onChange={(e) => handleGlobalDataChange('deliveryConfig', { ...globalData.deliveryConfig, poolarserverUrl: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none bg-white text-sm" placeholder="https://poolarserver.com/..." /></div>
                                )}

                                {/* FREISCHALTUNG der externen Freimeldung je Projekt
                                    (docs/freimeldung-online-plan.md §1.4a). Vorgabe
                                    ist AN — Betreiber-Entscheidung 28.10.2026: lieber
                                    abwählen als vergessen dazuzuwählen. Ein Projekt
                                    ohne Wert zeigt den Haken gesetzt
                                    (`istFreimeldungAktiv`); erst das Abwählen schreibt
                                    `false`. Ohne Häkchen entsteht beim Versand kein
                                    Link, es wird kein QR-Code gedruckt, und bereits
                                    gedruckte Codes sind ab sofort gesperrt (die RPCs
                                    ab Nr. 183 prüfen das Flag selbst). Ein Projekt
                                    lässt sich damit nachträglich stilllegen.
                                    Die Meldungen, die schon eingegangen sind, bleiben
                                    sichtbar und entscheidbar — `entscheiden` kennt den
                                    Schalter bewusst nicht. */}
                                <label className={`flex items-start gap-2.5 text-sm text-slate-700 ${isReadOnly ? 'opacity-60' : 'cursor-pointer'}`}>
                                    <input
                                        type="checkbox"
                                        disabled={isReadOnly}
                                        checked={istFreimeldungAktiv(metadata)}
                                        onChange={(e) => handleMetadataDirectChange('freimeldung', { ...(metadata.freimeldung || {}), aktiv: e.target.checked })}
                                        className="mt-0.5 rounded text-indigo-600 focus:ring-indigo-500"
                                    />
                                    <span>
                                        <span className="font-bold">Online-Freimeldung für dieses Projekt freischalten</span>
                                        <span className="block text-[11px] text-slate-500 mt-0.5">
                                            Empfänger des Begehungsberichts können Mängel über einen QR-Code im PDF als behoben melden oder mit Begründung zurückweisen. Standardmäßig an — abwählen, wenn der Bauherr das nicht wünscht. Ohne Häkchen wird kein QR-Code gedruckt und vorhandene Links sind gesperrt.
                                        </span>
                                    </span>
                                </label>

                                {/* Beweisfoto-Pflicht der externen Freimeldung
                                    (docs/freimeldung-online-plan.md §F). Steht hier
                                    und nicht in den Firmeneinstellungen: ob eine
                                    Firma ohne Foto freimelden darf, entscheidet der
                                    Bauherr DIESES Projekts. Vorgabe (kein Wert) ist
                                    optional — strenger ist eine bewusste Wahl.
                                    Der Wert wandert in `metadata.freimeldung` und
                                    wird von der oeffentlichen RPC gelesen; eine
                                    eigene Spalte gibt es bewusst nicht.
                                    NUR bedienbar, solange die Freimeldung überhaupt
                                    freigeschaltet ist: ohne Freigabe gibt es keine
                                    Meldung, an der eine Foto-Pflicht hängen könnte.
                                    Der gespeicherte Wert bleibt dabei erhalten — wer
                                    wieder freischaltet, findet seine Einstellung
                                    vor. */}
                                <label className={`flex items-start gap-2.5 text-sm text-slate-700 ${isReadOnly || !istFreimeldungAktiv(metadata) ? 'opacity-60' : 'cursor-pointer'}`}>
                                    <input
                                        type="checkbox"
                                        disabled={isReadOnly || !istFreimeldungAktiv(metadata)}
                                        checked={metadata.freimeldung?.fotoPflicht === true}
                                        onChange={(e) => handleMetadataDirectChange('freimeldung', { ...(metadata.freimeldung || {}), fotoPflicht: e.target.checked })}
                                        className="mt-0.5 rounded text-indigo-600 focus:ring-indigo-500"
                                    />
                                    <span>
                                        <span className="font-bold">Freimeldung online: Beweisfoto verpflichtend</span>
                                        <span className="block text-[11px] text-slate-500 mt-0.5">
                                            Wer einen Mangel über den QR-Code im Bericht als behoben meldet, muss ein Foto hochladen. Ohne Häkchen ist das Foto freiwillig.
                                        </span>
                                    </span>
                                </label>
                            </div>
                        </div>
                        
                        <ProjectCustomerLink
                            projectId={globalData.projectId}
                            darfZuordnen={darfKundeZuordnen}
                            contextCustomerName={metadata.customerName}
                            contextClientName={metadata.clientName}
                            isReadOnly={isReadOnly}
                        />

                        {zeigeNachtrag && (
                            <ProjectSupplementQuoteButton
                                projectId={globalData.projectId}
                                onOpenQuote={onOpenQuote}
                                isReadOnly={isReadOnly}
                            />
                        )}

                        <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 shadow-inner">
                            <h4 className="text-sm font-bold text-slate-700 flex items-center gap-2 mb-3"><Contact className={`w-4 h-4 ${isSifa ? 'text-teal-600' : 'text-blue-600'}`}/> {isSifa ? 'Unternehmen/Auftraggeber' : 'Auftraggeber'}</h4>
                            <div className="space-y-3">
                                <div className="space-y-1"><label className="text-[11px] font-bold text-slate-400 uppercase">Firma / Name</label><input type="text" value={localCustomer.name} onChange={(e) => setLocalCustomer({...localCustomer, name: e.target.value})} onBlur={() => syncToParent()} className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:border-blue-500 outline-none bg-white text-sm" placeholder="Musterfirma GmbH" /></div>
                                <div className="space-y-1"><label className="text-[11px] font-bold text-slate-400 uppercase">Anschrift</label><input type="text" value={localCustomer.address} onChange={(e) => setLocalCustomer({...localCustomer, address: e.target.value})} onBlur={() => syncToParent()} className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:border-blue-500 outline-none bg-white text-sm" placeholder="Straße, PLZ Ort" /></div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="space-y-1"><label className="text-[11px] font-bold text-slate-400 uppercase">Ansprechpartner</label><input type="text" value={localCustomer.contact} onChange={(e) => setLocalCustomer({...localCustomer, contact: e.target.value})} onBlur={() => syncToParent()} className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:border-blue-500 outline-none bg-white text-sm" /></div>
                                    <div className="space-y-1"><label className="text-[11px] font-bold text-slate-400 uppercase">Telefon</label><input type="text" value={localCustomer.phone} onChange={(e) => setLocalCustomer({...localCustomer, phone: e.target.value})} onBlur={() => syncToParent()} className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:border-blue-500 outline-none bg-white text-sm" /></div>
                                </div>
                                <div className="space-y-1"><label className="text-[11px] font-bold text-slate-400 uppercase">E-Mail</label><input type="text" value={localCustomer.email} onChange={(e) => setLocalCustomer({...localCustomer, email: e.target.value})} onBlur={() => syncToParent()} className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:border-blue-500 outline-none bg-white text-sm" /></div>
                            </div>
                        </div>

                        <div className="bg-amber-50 p-4 rounded-xl border border-amber-100 shadow-inner">
                            <h4 className="text-sm font-bold text-amber-900 flex items-center gap-2 mb-3"><HardHat className="w-4 h-4 text-amber-600"/> {isSifa ? 'Ansprechpartner im Betrieb' : 'Ansprechpartner vor Ort'}</h4>
                            <div className="space-y-3">
                                <div className="space-y-1"><label className="text-[11px] font-bold text-amber-700/60 uppercase">Firma / Name</label><input type="text" value={localSiteContact.company} onChange={(e) => setLocalSiteContact({...localSiteContact, company: e.target.value})} onBlur={() => syncToParent()} className="w-full px-3 py-2 border border-amber-200 rounded-lg focus:border-amber-500 outline-none bg-white text-sm" placeholder={isSifa ? 'Abteilung / Standort' : 'Bauleitung XY'} /></div>
                                <div className="space-y-1"><label className="text-[11px] font-bold text-amber-700/60 uppercase">Anschrift</label><input type="text" value={localSiteContact.address} onChange={(e) => setLocalSiteContact({...localSiteContact, address: e.target.value})} onBlur={() => syncToParent()} className="w-full px-3 py-2 border border-amber-200 rounded-lg focus:border-amber-500 outline-none bg-white text-sm" placeholder={isSifa ? 'Straße, PLZ Ort' : 'Baubüro'} /></div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="space-y-1"><label className="text-[11px] font-bold text-amber-700/60 uppercase">Ansprechpartner</label><input type="text" value={localSiteContact.person} onChange={(e) => setLocalSiteContact({...localSiteContact, person: e.target.value})} onBlur={() => syncToParent()} className="w-full px-3 py-2 border border-amber-200 rounded-lg focus:border-amber-500 outline-none bg-white text-sm" /></div>
                                    <div className="space-y-1"><label className="text-[11px] font-bold text-amber-700/60 uppercase">Telefon</label><input type="text" value={localSiteContact.phone} onChange={(e) => setLocalSiteContact({...localSiteContact, phone: e.target.value})} onBlur={() => syncToParent()} className="w-full px-3 py-2 border border-amber-200 rounded-lg focus:border-amber-500 outline-none bg-white text-sm" /></div>
                                </div>
                                <div className="space-y-1"><label className="text-[11px] font-bold text-amber-700/60 uppercase">E-Mail</label><input type="text" value={localSiteContact.email} onChange={(e) => setLocalSiteContact({...localSiteContact, email: e.target.value})} onBlur={() => syncToParent()} className="w-full px-3 py-2 border border-amber-200 rounded-lg focus:border-amber-500 outline-none bg-white text-sm" /></div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
